import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Avatar,
  Radio,
} from '@mui/material';
import { 
  Search as SearchIcon,
  Visibility,
  Close,
  Receipt as ReceiptIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Person,
  Edit,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload,
} from '@mui/icons-material';
import { getTransactionReports } from '../../services/reports';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import FormatBadge from '../../components/FormatBadge/FormatBadge';
import { showSnackbar } from '../../components/Snackbar';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const BookingReceipt = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    paymentMode: '',
    searchTerm: '',
    bookingId: '',
    date: '',
  });

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [customerReceiptsDialogOpen, setCustomerReceiptsDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('receiptDateDesc');
  const [tempSortBy, setTempSortBy] = useState('receiptDateDesc');
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');
  const [tempDate, setTempDate] = useState('');
  const [tempPaymentMode, setTempPaymentMode] = useState('');
  const [tempBookingId, setTempBookingId] = useState('');

  useEffect(() => {
    if (moreFiltersOpen) {
      setTempSortBy(sortBy);
      setTempFromDate(filters.fromDate || '');
      setTempToDate(filters.toDate || '');
      setTempDate(filters.date || '');
      setTempPaymentMode(filters.paymentMode || '');
      setTempBookingId(filters.bookingId || '');
    }
  }, [moreFiltersOpen, sortBy, filters]);

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const [searchCache, setSearchCache] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [totalReceipts, setTotalReceipts] = useState(0);
  const [receiptsLoading, setReceiptsLoading] = useState(false);

  const sampleData = [
    // {
    //   sno: 1,
    //   receiptNo: 'RCP001',
    //   receiptDate: '2025-01-01',
    //   bookingId: 'BK001',
    //   customerName: 'Amit Gupta',
    //   amount: 100000,
    //   paymentMode: 'Cash',
    //   status: 'Confirmed',
    // },
    // {
    //   sno: 2,
    //   receiptNo: 'RCP002',
    //   receiptDate: '2025-01-15',
    //   bookingId: 'BK002',
    //   customerName: 'Jane Smith',
    //   amount: 50000,
    //   paymentMode: 'Bank Transfer',
    //   status: 'Pending',
    // },
    // {
    //   sno: 3,
    //   receiptNo: 'RCP003',
    //   receiptDate: '2025-01-20',
    //   bookingId: 'BK003',
    //   customerName: 'Mike Johnson',
    //   amount: 75000,
    //   paymentMode: 'Cheque',
    //   status: 'Confirmed',
    // },
    // {
    //   sno: 4,
    //   receiptNo: 'RCP004',
    //   receiptDate: '2025-01-25',
    //   bookingId: 'BK004',
    //   customerName: 'Amit Gupta',
    //   amount: 150000,
    //   paymentMode: 'UPI',
    //   status: 'Confirmed',
    // },
    // {
    //   sno: 5,
    //   receiptNo: 'RCP005',
    //   receiptDate: '2025-01-28',
    //   bookingId: 'BK005',
    //   customerName: 'Amit Gupta',
    //   amount: 80000,
    //   paymentMode: 'Bank Transfer',
    //   status: 'Pending',
    // },
    // {
    //   sno: 6,
    //   receiptNo: 'RCP006',
    //   receiptDate: '2025-02-01',
    //   bookingId: 'BK006',
    //   customerName: 'Jane Smith',
    //   amount: 120000,
    //   paymentMode: 'Cash',
    //   status: 'Confirmed',
    // },
  ];

  useEffect(() => {
    if (!hasSearched && !searchLoading && !receiptsLoading) {
      handleSearch(0, rowsPerPage);
    }
  }, [hasSearched, searchLoading, receiptsLoading]);

  useEffect(() => {
    if (hasSearched) {
      handleSearch(0, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.paymentMode]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (hasSearched) {
      handleSearch(newPage, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    if (hasSearched) {
      handleSearch(0, newLimit);
    }
  };

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(filters.fromDate || '');
    setExportToDate(filters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      const apiParams = {
        export: 'true',
      };

      if (filters.status) {
        apiParams.receiptDisplayStatus = filters.status;
      }
      if (filters.paymentMode) {
        apiParams.paymentMode = filters.paymentMode.toLowerCase();
      }
      if (filters.bookingId) {
        apiParams.bookingId = filters.bookingId;
      }
      if (searchTerm.trim()) {
        apiParams.search = searchTerm.trim();
      }

      // Sort Params
      let apiSortBy = 'receiptDate';
      let apiSortOrder = 'desc';
      if (sortBy) {
        if (sortBy.endsWith('Desc')) {
          apiSortBy = sortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (sortBy.endsWith('Asc')) {
          apiSortBy = sortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      apiParams.sortBy = apiSortBy;
      apiParams.sortOrder = apiSortOrder;

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        if (exportDateMode === 'current') {
          if (filters.fromDate) apiParams.fromDate = filters.fromDate;
          if (filters.toDate) apiParams.toDate = filters.toDate;
          if (filters.date) apiParams.date = filters.date;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) apiParams.fromDate = exportFromDate;
          if (exportToDate) apiParams.toDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            apiParams.fromDate = exportFromDate;
            apiParams.toDate = exportFromDate;
          }
        }
      }

      const response = await getTransactionReports(apiParams);

      let receiptsData = [];
      if (response && response.data && Array.isArray(response.data.docs)) {
        receiptsData = response.data.docs;
      } else if (response && Array.isArray(response.data)) {
        receiptsData = response.data;
      } else if (Array.isArray(response)) {
        receiptsData = response;
      }

      if (receiptsData.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const transformedReceipts = receiptsData.map((receipt, index) => {
        const rawDate = receipt.receiptDate || '';
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString() : '';

        return {
          sno: index + 1,
          receiptId: receipt.receiptId || `RCP${String(index + 1).padStart(3, '0')}`,
          receiptDate: formattedDate,
          bookingId: receipt.bookingId?.bookingId || receipt.bookingId || '',
          customerName: receipt.customerName || receipt.bookingId?.customer?.name || '',
          amount: receipt.amount || 0,
          paymentMode: receipt.paymentMode || receipt.mode || '',
          status: (receipt.isRefund === true || receipt.receiptDisplayStatus === 'Refund') ? 'Refund' :
                  receipt.status === 'approved' ? 'Confirmed' : 
                  receipt.status === 'confirmed' ? 'Confirmed' : 
                  receipt.status === 'pending' ? 'Pending' : 
                  receipt.status === 'cancelled' ? 'Cancelled' : 
                  receipt.status === 'refund' ? 'Refund' : 
                  (receipt.status ? receipt.status.charAt(0).toUpperCase() + receipt.status.slice(1) : 'Pending'),
        };
      });

      const rows = transformedReceipts.map(receipt => ({
        'S.No': receipt.sno,
        'Receipt No': receipt.receiptId,
        'Receipt Date': receipt.receiptDate,
        'Booking ID': receipt.bookingId,
        'Customer Name': receipt.customerName,
        'Amount': receipt.amount || 0,
        'Payment Mode': receipt.paymentMode || '',
        'Status': receipt.status || ''
      }));

      const fileName = `booking_receipts_${new Date().toISOString().split('T')[0]}`;

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        const formattedRows = rows.map(r => ({
          ...r,
          'Amount': `₹${r['Amount'].toLocaleString('en-IN')}`,
        }));
        exportAsPDF({ rows: formattedRows, title: 'Booking Receipt Report', fileNameBase: fileName, orientation: 'l' });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: 'Booking Receipts', fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  // View and Edit action handlers
  const handleView = (receipt) => {
    setSelectedReceipt(receipt);
    setViewDialogOpen(true);
  };

  const handleViewCustomerReceipts = (customerName) => {
    setSelectedCustomer(customerName);
    setCustomerReceiptsDialogOpen(true);
  };

  const handleEdit = (receipt) => {
    setSelectedReceipt(receipt);
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedReceipt(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedReceipt(null);
  };

  const handleCloseCustomerReceiptsDialog = () => {
    setCustomerReceiptsDialogOpen(false);
    setSelectedCustomer(null);
  };

  // Optimized filter function with memoization and caching - only works when search is performed
  const getFilteredData = useCallback(() => {
    return receipts;
  }, [receipts]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalAmount = filteredData.reduce((sum, item) => sum + item.amount, 0);
  const confirmedAmount = filteredData.filter(item => item.status === 'Confirmed').reduce((sum, item) => sum + item.amount, 0);
  const pendingAmount = filteredData.filter(item => item.status === 'Pending').reduce((sum, item) => sum + item.amount, 0);

  // Get customer receipts
  const getCustomerReceipts = (customerName) => {
    return sampleData.filter(receipt => receipt.customerName === customerName);
  };

  const customerReceipts = selectedCustomer ? getCustomerReceipts(selectedCustomer) : [];
  const customerTotalAmount = customerReceipts.reduce((sum, item) => sum + item.amount, 0);
  const customerConfirmedAmount = customerReceipts.filter(item => item.status === 'Confirmed').reduce((sum, item) => sum + item.amount, 0);
  const customerPendingAmount = customerReceipts.filter(item => item.status === 'Pending').reduce((sum, item) => sum + item.amount, 0);

  // No automatic search - only when button is clicked

  // Main search button handler - applies all filters and fetches data from API
  const handleSearch = useCallback(async (p = page, limit = rowsPerPage, overrides = {}) => {
    setSearchLoading(true);
    setReceiptsLoading(true);
    setHasSearched(true);

    try {
      const apiParams = {
        page: p + 1,
        limit: limit,
      };

      const {
        fromDate: oFromDate,
        toDate: oToDate,
        status: oStatus,
        paymentMode: oPaymentMode,
        searchTerm: oSearchTerm,
        bookingId: oBookingId,
        date: oDate,
        sortBy: oSortBy,
      } = overrides;

      const effFromDate = oFromDate !== undefined ? oFromDate : filters.fromDate;
      const effToDate = oToDate !== undefined ? oToDate : filters.toDate;
      const effStatus = oStatus !== undefined ? oStatus : filters.status;
      const effPaymentMode = oPaymentMode !== undefined ? oPaymentMode : filters.paymentMode;
      const effSearchTerm = oSearchTerm !== undefined ? oSearchTerm : searchTerm;
      const effBookingId = oBookingId !== undefined ? oBookingId : filters.bookingId;
      const effDate = oDate !== undefined ? oDate : filters.date;
      const effSortBy = oSortBy !== undefined ? oSortBy : sortBy;

      if (effFromDate) {
        apiParams.fromDate = effFromDate;
      }
      if (effToDate) {
        apiParams.toDate = effToDate;
      }
      if (effDate) {
        apiParams.date = effDate;
      }
      if (effStatus) {
        apiParams.receiptDisplayStatus = effStatus;
      }
      if (effPaymentMode) {
        apiParams.paymentMode = effPaymentMode.toLowerCase();
      }
      if (effBookingId) {
        apiParams.bookingId = effBookingId;
      }
      if (effSearchTerm && effSearchTerm.trim()) {
        apiParams.search = effSearchTerm.trim();
      }

      let apiSortBy = 'receiptDate';
      let apiSortOrder = 'desc';
      if (effSortBy) {
        if (effSortBy.endsWith('Desc')) {
          apiSortBy = effSortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (effSortBy.endsWith('Asc')) {
          apiSortBy = effSortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }
      apiParams.sortBy = apiSortBy;
      apiParams.sortOrder = apiSortOrder;

      const response = await getTransactionReports(apiParams);

      let receiptsData = [];
      let totalCount = 0;

      if (response && response.data && Array.isArray(response.data.docs)) {
        receiptsData = response.data.docs;
        totalCount = response.data.totalDocs || 0;
      } else if (response && Array.isArray(response.data)) {
        receiptsData = response.data;
        totalCount = response.total || response.totalCount || response.data.length;
      } else if (Array.isArray(response)) {
        receiptsData = response;
        totalCount = response.length;
      }

      const transformedReceipts = receiptsData.map((receipt, index) => {
        const rawDate = receipt.receiptDate || '';
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString() : '';

        return {
          sno: (p * limit) + index + 1,
          receiptId: receipt.receiptId || `RCP${String((p * limit) + index + 1).padStart(3, '0')}`,
          receiptDate: formattedDate,
          bookingId: receipt.bookingId?.bookingId || receipt.bookingId || '',
          customerName: receipt.customerName || receipt.bookingId?.customer?.name || '',
          amount: receipt.amount || 0,
          paymentMode: receipt.paymentMode || receipt.mode || '',
          status: (receipt.isRefund === true || receipt.receiptDisplayStatus === 'Refund') ? 'Refund' :
                  receipt.status === 'approved' ? 'Confirmed' : 
                  receipt.status === 'confirmed' ? 'Confirmed' : 
                  receipt.status === 'pending' ? 'Pending' : 
                  receipt.status === 'cancelled' ? 'Cancelled' : 
                  receipt.status === 'refund' ? 'Refund' : 
                  (receipt.status ? receipt.status.charAt(0).toUpperCase() + receipt.status.slice(1) : 'Pending'),
          originalData: receipt,
        };
      });

      setReceipts(transformedReceipts);
      setTotalReceipts(totalCount);

    } catch (error) {
      setReceipts([]);
      setTotalReceipts(0);
    } finally {
      setSearchLoading(false);
      setReceiptsLoading(false);
    }
  }, [page, rowsPerPage, filters, searchTerm, sortBy]);

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    const updatedFilters = {
      ...filters,
      fromDate: tempFromDate,
      toDate: tempToDate,
      date: tempDate,
      paymentMode: tempPaymentMode,
      bookingId: tempBookingId,
    };
    setFilters(updatedFilters);
    setPage(0);
    setMoreFiltersOpen(false);
    handleSearch(0, rowsPerPage, {
      fromDate: tempFromDate,
      toDate: tempToDate,
      date: tempDate,
      paymentMode: tempPaymentMode,
      bookingId: tempBookingId,
      sortBy: tempSortBy,
    });
  };

  const handleResetFilters = () => {
    setSortBy('receiptDateDesc');
    setTempSortBy('receiptDateDesc');
    setTempFromDate('');
    setTempToDate('');
    setTempDate('');
    setTempPaymentMode('');
    setTempBookingId('');
    
    const cleared = {
      ...filters,
      fromDate: '',
      toDate: '',
      date: '',
      paymentMode: '',
      bookingId: '',
    };
    setFilters(cleared);
    setPage(0);
    handleSearch(0, rowsPerPage, {
      fromDate: '',
      toDate: '',
      date: '',
      paymentMode: '',
      bookingId: '',
      sortBy: 'receiptDateDesc',
    });
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters(prev => ({ ...prev, searchTerm: '' }));
    setHasSearched(false);
  };

  // Cache cleanup effect - clear cache when it gets too large
  useEffect(() => {
    const cacheKeys = Object.keys(searchCache);
    if (cacheKeys.length > 50) { // Limit cache to 50 entries
      const oldestKeys = cacheKeys.slice(0, 25); // Remove oldest 25 entries
      setSearchCache(prev => {
        const newCache = { ...prev };
        oldestKeys.forEach(key => delete newCache[key]);
        return newCache;
      });
    }
  }, [searchCache]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Booking Receipt Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage booking receipts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={() => handleOpenExportDialog('pdf')}
              onExportCsv={() => handleOpenExportDialog('csv')}
              onExportExcel={() => handleOpenExportDialog('excel')}
            />
            <RefreshButton
              onClick={() => {
                handleSearch(page, rowsPerPage);
              }}
              loading={searchLoading || receiptsLoading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CurrencyRupeeIcon sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                ₹{totalAmount.toLocaleString('en-IN')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Receipts
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                ₹{confirmedAmount.toLocaleString('en-IN')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Confirmed Receipts
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <ScheduleIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                ₹{pendingAmount.toLocaleString('en-IN')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Pending Receipts
            </Typography>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Receipt Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3.5}>
              <TextField
                fullWidth
                size="small"
                label="Search Receipts"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by receipt no..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchLoading ? (
                        <CircularProgress size={20} color="primary" />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  label="Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="Refund">Refund</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4} md={1.2}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleSearch()}
                disabled={searchLoading}
                sx={{
                  height: '40px',
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Search'
                )}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={1.1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setFilters({
                    fromDate: '',
                    toDate: '',
                    status: '',
                    paymentMode: '',
                    searchTerm: '',
                    bookingId: '',
                    date: '',
                  });
                  setSearchTerm('');
                  setSortBy('receiptDateDesc');
                  setTempSortBy('receiptDateDesc');
                  setTempFromDate('');
                  setTempToDate('');
                  setTempDate('');
                  setTempPaymentMode('');
                  setTempBookingId('');
                  setHasSearched(false);
                  setSearchLoading(false);
                  setPage(0);
                  handleSearch(0, rowsPerPage, {
                    fromDate: '',
                    toDate: '',
                    status: '',
                    paymentMode: '',
                    searchTerm: '',
                    bookingId: '',
                    date: '',
                    sortBy: 'receiptDateDesc',
                  });
                }}
                sx={{ 
                  height: '40px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#3F51B5',
                    backgroundColor: '#f0f7ff',
                  }
                }}
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2.7}>
              <Box sx={{ width: '100%' }}>
                <MoreFilters
                  expanded={moreFiltersOpen}
                  onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                  sortBy={tempSortBy}
                  onChangeSortBy={setTempSortBy}
                  onApply={handleApplyFilters}
                  onReset={handleResetFilters}
                  type="bookingReceipt"
                  height="40px"
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempFromDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempFromDate(val);
                          if (val) setTempDate('');
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempToDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempToDate(val);
                          if (val) setTempDate('');
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={tempDate}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTempDate(val);
                          if (val) {
                            setTempFromDate('');
                            setTempToDate('');
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Booking ID"
                        value={tempBookingId}
                        onChange={(e) => setTempBookingId(e.target.value)}
                        placeholder="Enter Booking ID..."
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                          label="Payment Mode"
                          value={tempPaymentMode}
                          onChange={(e) => setTempPaymentMode(e.target.value)}
                        >
                          <MenuItem value="">All Payment Modes</MenuItem>
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                          <MenuItem value="UPI">UPI</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </MoreFilters>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search Results Summary */}


      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Receipt Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Receipt No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Receipt Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Payment Mode</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(searchLoading || receiptsLoading) ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          {searchLoading ? 'Searching receipts...' : 'Loading receipts...'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : !hasSearched ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          Ready to View Receipts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Apply filters above and click Search to view booking receipts
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.map((row) => (
                  <TableRow 
                    key={row.sno} 
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                      '&:hover': { backgroundColor: '#f0f7ff !important' }
                    }}
                  >
                    <TableCell>{row.sno}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.receiptId}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.receiptDate}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bookingId}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.customerName}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{row.amount.toLocaleString('en-IN')}</TableCell>
                    <TableCell>{row.paymentMode}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status} 
                        size="small" 
                        color={row.status === 'Confirmed' ? 'success' : row.status === 'Cancelled' ? 'error' : row.status === 'Refund' ? 'error' : 'warning'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Receipt Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(row)}
                            sx={{ color: '#536DFE' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="View All Receipts for Customer">
                          <IconButton
                            size="small"
                            onClick={() => handleViewCustomerReceipts(row.customerName)}
                            sx={{ color: '#4caf50' }}
                          >
                            <Person fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No receipts found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {hasSearched && totalReceipts > 0 && (
            <TablePagination
              
              component="div"
              count={totalReceipts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon />
            <Typography variant="h6" fontWeight="bold">
              Receipt Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedReceipt && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <ReceiptIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedReceipt.receiptId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer: {selectedReceipt.customerName}
                  </Typography>
                  <Chip 
                    label={selectedReceipt.status} 
                    size="small" 
                    color={selectedReceipt.status === 'Confirmed' ? 'success' : selectedReceipt.status === 'Cancelled' ? 'error' : selectedReceipt.status === 'Refund' ? 'error' : 'warning'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Receipt Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="primary" />
                        Receipt Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Receipt No:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReceipt.receiptId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Receipt Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReceipt.receiptDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReceipt.bookingId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Customer Name:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReceipt.customerName}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Payment Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CurrencyRupeeIcon color="primary" />
                        Payment Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">₹{selectedReceipt.amount.toLocaleString('en-IN')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Payment Mode:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedReceipt.paymentMode}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip 
                            label={selectedReceipt.status} 
                            size="small" 
                            color={selectedReceipt.status === 'Confirmed' ? 'success' : selectedReceipt.status === 'Cancelled' ? 'error' : selectedReceipt.status === 'Refund' ? 'error' : 'warning'}
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Receipt Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#ff9800',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            <Typography variant="h6" fontWeight="bold">
              Edit Receipt
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedReceipt && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Edit functionality will be implemented here. Currently showing receipt details for: <strong>{selectedReceipt.receiptId}</strong>
              </Typography>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Receipt No:</strong> {selectedReceipt.receiptId}<br/>
                  <strong>Customer:</strong> {selectedReceipt.customerName}<br/>
                  <strong>Amount:</strong> ₹{selectedReceipt.amount.toLocaleString('en-IN')}<br/>
                  <strong>Status:</strong> {selectedReceipt.status}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCloseEditDialog}
            variant="contained"
            sx={{ 
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Receipts Dialog */}
      <Dialog
        open={customerReceiptsDialogOpen}
        onClose={handleCloseCustomerReceiptsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person />
            <Typography variant="h6" fontWeight="bold">
              All Receipts for {selectedCustomer}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseCustomerReceiptsDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedCustomer && (
            <>
              {/* Customer Summary */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedCustomer}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {customerReceipts.length} receipt{customerReceipts.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    ₹{customerTotalAmount.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>

              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e9' }}>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      ₹{customerConfirmedAmount.toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Confirmed Amount
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0' }}>
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      ₹{customerPendingAmount.toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending Amount
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd' }}>
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                      {customerReceipts.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Receipts
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Receipts Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    Receipt Details
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#536DFE' }}>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Receipt No</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Receipt Date</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Amount</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Payment Mode</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerReceipts.map((receipt, index) => (
                          <TableRow 
                            key={receipt.sno} 
                            hover
                            sx={{
                              '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                              '&:hover': { backgroundColor: '#f0f7ff !important' }
                            }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{receipt.receiptId}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{receipt.receiptDate}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{receipt.bookingId}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>₹{receipt.amount.toLocaleString('en-IN')}</TableCell>
                            <TableCell>{receipt.paymentMode}</TableCell>
                            <TableCell>
                              <Chip 
                                label={receipt.status} 
                                size="small" 
                                color={receipt.status === 'Confirmed' ? 'success' : receipt.status === 'Cancelled' ? 'error' : receipt.status === 'Refund' ? 'error' : 'warning'}
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseCustomerReceiptsDialog}
            variant="contained"
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export Booking Receipt Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filters.fromDate && filters.toDate
                    ? `${filters.fromDate} → ${filters.toDate}`
                    : filters.fromDate ? `From ${filters.fromDate}` : filters.toDate ? `Till ${filters.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default BookingReceipt;

